import {ChevronLeft, ChevronRight} from '@mui/icons-material';
import {Button, ButtonGroup, FormControlLabel, Paper, Stack, Switch, Typography} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';
import {ExamModule, ModuleTopic} from '@cp/model';
import BondModuleTopicDropdown from '../../../bonds/bondModuleTopicDropdown/BondModuleTopicDropdown';
import {useGetModuleTopicsWithStudy} from '../../../hooks/study/useGetModuleTopicsWithStudy';

export interface AdminStudyModuleProps {
  examModules: ExamModule[];
  selectedModule?: ExamModule;
  handleModuleChange: (moduleId?: string) => void;
  selectedTopic?: ModuleTopic;
  handleTopicChange: (topic?: string) => void;
}

export default function AdminStudyModule({
  examModules,
  selectedModule,
  selectedTopic,
  handleModuleChange,
  handleTopicChange,
}: AdminStudyModuleProps) {
  const [hideTopicsWithStudy, setHideTopicsWithStudy] = useState(false);
  const {
    getModuleTopicsWithStudy,
    topics: topicsWithStudy,
    loading: topicsWithStudyLoading,
  } = useGetModuleTopicsWithStudy();

  // Clear switch state when module changes
  useEffect(() => {
    setHideTopicsWithStudy(false);
  }, [selectedModule?.id]);

  // Fetch topics with study when switch is enabled
  useEffect(() => {
    if (hideTopicsWithStudy && selectedModule?.id) {
      getModuleTopicsWithStudy(selectedModule.id).catch((error) => {
        console.error('Failed to fetch topics with study:', error);
      });
    }
  }, [hideTopicsWithStudy, selectedModule?.id, getModuleTopicsWithStudy]);

  // Calculate available topics considering the hideTopics filter
  const availableTopics = useMemo(() => {
    if (!selectedModule) return [];
    const baseTopics = selectedModule.topics || [];
    if (!hideTopicsWithStudy || !topicsWithStudy || topicsWithStudy.length === 0) {
      return baseTopics;
    }
    const hideTopicNames = new Set(topicsWithStudy.map((t) => t.topic));
    return baseTopics.filter((t) => !hideTopicNames.has(t.topic));
  }, [selectedModule, hideTopicsWithStudy, topicsWithStudy]);

  // Find current topic index
  const currentTopicIndex = useMemo(() => {
    if (!selectedTopic?.topic) return -1;
    return availableTopics.findIndex((t) => t.topic === selectedTopic.topic);
  }, [selectedTopic, availableTopics]);

  // Navigation handlers
  const handlePreviousTopic = () => {
    if (currentTopicIndex > 0) {
      const previousTopic = availableTopics[currentTopicIndex - 1];
      handleTopicChange(previousTopic.topic);
    }
  };

  const handleNextTopic = () => {
    if (currentTopicIndex < availableTopics.length - 1) {
      const nextTopic = availableTopics[currentTopicIndex + 1];
      handleTopicChange(nextTopic.topic);
    }
  };

  const canGoPrevious = currentTopicIndex > 0;
  const canGoNext = currentTopicIndex >= 0 && currentTopicIndex < availableTopics.length - 1;

  return (
    <Paper sx={{p: 3}}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          {selectedModule && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={hideTopicsWithStudy}
                    onChange={(e) => setHideTopicsWithStudy(e.target.checked)}
                    disabled={topicsWithStudyLoading || !selectedModule?.id}
                  />
                }
                label="Hide Topics With Study"
              />
              {selectedTopic && availableTopics.length > 1 && (
                <ButtonGroup variant="outlined" size="small">
                  <Button
                    onClick={handlePreviousTopic}
                    disabled={!canGoPrevious}
                    startIcon={<ChevronLeft />}
                    sx={{minWidth: 'auto'}}
                  >
                    Prev
                  </Button>
                  <Button
                    onClick={handleNextTopic}
                    disabled={!canGoNext}
                    endIcon={<ChevronRight />}
                    sx={{minWidth: 'auto'}}
                  >
                    Next
                  </Button>
                </ButtonGroup>
              )}
            </>
          )}
        </Stack>

        <BondModuleTopicDropdown
          moduleId={selectedModule?.id}
          topic={selectedTopic?.topic}
          examModules={examModules}
          topicRequired
          hideTopicWhenNoModule
          onModuleChange={handleModuleChange}
          onTopicChange={handleTopicChange}
          hideTopics={hideTopicsWithStudy ? topicsWithStudy : undefined}
        />
        {selectedModule && (
          <Paper sx={{p: 2, mb: 2, border: '1px solid', borderColor: 'info.main'}}>
            <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
              Topic Detail
            </Typography>
            <Typography variant="body2" sx={{whiteSpace: 'pre-wrap'}}>
              {selectedTopic?.detail}
            </Typography>
          </Paper>
        )}
      </Stack>
    </Paper>
  );
}
